import React, { useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { useSearchParams, useNavigate, Link } from 'react-router-dom';
import { Col, Row, Card, Container, Spinner, Alert, Form, Button } from 'react-bootstrap';
import { ArrowNarrowLeftIcon } from '@heroicons/react/solid';

import { Routes } from '#Routes';
import {
  selectIsLogged,
  useFetchUserPreferencesQuery,
  useUpdateMarketingPreferencesMutation,
  useUnsubscribeByTokenMutation,
} from '#store';
import { baseApi } from '#store/apis/baseApi';

const EmailPreferencesPage = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const isLogged = useSelector(selectIsLogged);

  const token = searchParams.get('token');
  const email = searchParams.get('email');

  const [unsubscribed, setUnsubscribed] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState({ type: null, text: null });

  // Fetch user preferences if logged in
  const { data: userData, isLoading: isLoadingPreferences } = useFetchUserPreferencesQuery(undefined, {
    skip: !isLogged || (token && email), // Skip if not logged in or if we have token (will unsubscribe via token)
  });

  const [updateMarketingPreferences] = useUpdateMarketingPreferencesMutation();
  const [unsubscribeByToken] = useUnsubscribeByTokenMutation();

  // If token and email are in URL, automatically unsubscribe
  useEffect(() => {
    if (token && email) {
      handleUnsubscribeFromToken(token, email);
    } else if (isLogged && userData) {
      // If user is logged in, load their preferences
      setUnsubscribed(userData.marketing_emails_unsubscribed || false);
      setLoading(false);
    } else if (!isLogged && !token) {
      // If not logged in and no token, just show the form (but it won't work)
      setLoading(false);
    }
  }, [token, email, isLogged, userData]);

  const handleUnsubscribeFromToken = async (tokenValue, emailValue) => {
    try {
      setLoading(true);
      setMessage({ type: null, text: null });

      const result = await unsubscribeByToken({
        token: tokenValue,
        email: emailValue,
      }).unwrap();

      if (result.success) {
        setUnsubscribed(true);
        setMessage({
          type: 'success',
          text: result.message || t('emailPreferences.unsubscribeSuccess'),
        });
        // Invalidate cache if user is logged in to refresh AccountDetails
        if (isLogged) {
          dispatch(baseApi.util.invalidateTags(['UserPreferences']));
        }
      } else {
        setMessage({
          type: 'error',
          text: result.error_message || t('emailPreferences.unsubscribeError'),
        });
      }
    } catch (error) {
      const errorMessage = getErrorMessage(error);
      setMessage({
        type: 'error',
        text: errorMessage,
      });
    } finally {
      setLoading(false);
    }
  };

  const getErrorMessage = (error) => {
    const status = error?.status || error?.data?.status_code;
    const errorData = error?.data;

    switch (status) {
      case 400:
        return errorData?.error_message || t('emailPreferences.errorInvalidToken');
      case 404:
        return t('emailPreferences.errorUserNotFound');
      case 429:
        return t('emailPreferences.errorTooManyAttempts');
      case 500:
        return t('emailPreferences.errorServer');
      default:
        return errorData?.error_message || t('emailPreferences.unsubscribeError');
    }
  };

  const handleToggleSubscription = async (value) => {
    if (!isLogged) {
      setMessage({
        type: 'error',
        text: t('emailPreferences.mustBeLoggedIn'),
      });
      return;
    }

    try {
      setSaving(true);
      setMessage({ type: null, text: null });

      const result = await updateMarketingPreferences({
        marketing_emails_unsubscribed: value,
      }).unwrap();

      setUnsubscribed(result.marketing_emails_unsubscribed);
      setMessage({
        type: 'success',
        text: value
          ? t('emailPreferences.unsubscribedSuccess')
          : t('emailPreferences.subscribedSuccess'),
      });
    } catch (error) {
      const errorMessage =
        error?.data?.error_message || t('emailPreferences.updateError');
      setMessage({
        type: 'error',
        text: errorMessage,
      });
    } finally {
      setSaving(false);
    }
  };

  if (loading || isLoadingPreferences) {
    return (
      <main>
        <section className='d-flex align-items-center vh-lg-100 mt-5 mt-lg-0 bg-soft'>
          <Container>
            <Row className='justify-content-center'>
              <Col xs={12} className='d-flex align-items-center justify-content-center'>
                <div className='bg-white shadow border-0 rounded border-light p-4 p-lg-5 w-100 fmxw-500'>
                  <div className='text-center'>
                    <Spinner animation='border' />
                    <p className='mt-3'>{t('emailPreferences.loading')}</p>
                  </div>
                </div>
              </Col>
            </Row>
          </Container>
        </section>
      </main>
    );
  }

  return (
    <main>
      <section className='d-flex align-items-center vh-lg-100 mt-5 mt-lg-0 bg-soft'>
        <Container>
          <Row className='justify-content-center'>
            <p className='text-center'>
              <Card.Link
                as={Link}
                to={isLogged ? Routes.Account.path : Routes.Login.path}
                className='d-flex align-items-center justify-content-center'
              >
                <ArrowNarrowLeftIcon className='icon icon-xs me-2' />
                {isLogged ? t('auth.backToAccount') : t('auth.backToLogin')}
              </Card.Link>
            </p>
            <Col xs={12} className='d-flex align-items-center justify-content-center'>
              <div className='bg-white shadow border-0 rounded border-light p-4 p-lg-5 w-100 fmxw-500'>
                <h3>{t('emailPreferences.title')}</h3>
                <p className='mb-4'>{t('emailPreferences.description')}</p>

                {message.type && (
                  <Alert
                    variant={message.type === 'success' ? 'success' : 'danger'}
                    dismissible
                    onClose={() => setMessage({ type: null, text: null })}
                    className='mb-4'
                  >
                    {message.text}
                  </Alert>
                )}

                <Form>
                  <Form.Check
                    type='checkbox'
                    id='marketing-emails'
                    checked={!unsubscribed}
                    onChange={(e) => handleToggleSubscription(!e.target.checked)}
                    disabled={saving || (token && email)}
                    label={t('emailPreferences.wantToReceiveEmails')}
                    className='mb-3'
                  />

                  <p className='text-muted small'>
                    {unsubscribed
                      ? t('emailPreferences.unsubscribedHelpText')
                      : t('emailPreferences.subscribedHelpText')}
                  </p>
                </Form>

                {token && email && (
                  <div className='mt-4 p-3 bg-light rounded'>
                    <p className='mb-2 small text-muted'>
                      {t('emailPreferences.unsubscribedViaLink')}
                    </p>
                    {isLogged && (
                      <Button
                        variant='logo-green'
                        size='sm'
                        onClick={() => navigate(Routes.Account.path)}
                      >
                        {t('emailPreferences.goToAccountSettings')}
                      </Button>
                    )}
                  </div>
                )}
              </div>
            </Col>
          </Row>
        </Container>
      </section>
    </main>
  );
};

export default EmailPreferencesPage;

